// Group Ratio line-item map (Build P2d). Maps (service type, ratio, band) → the priced NDIS
// support item that the group money engine uses for Auto-Ratio. If a row is missing, the engine
// falls back to (1:1 rate ÷ ratio) and flags the line as an estimate.
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, X, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';

const DAYS = ['weekday', 'saturday', 'sunday', 'public_holiday'];
const TIMES = ['daytime', 'evening', 'night'];
const blank = { serviceType: 'Community Access', ratioDen: 2, dayOfWeek: 'weekday', timeOfDay: 'daytime', supportItemNumber: '', supportItemName: '', rate: '' };

export default function ServiceRatios() {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);   // {editing?} | null
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/groups/service-ratio-items'); setItems(data || []); }
    catch { toast.error('Failed to load ratio items'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.serviceType.trim()) return toast.error('Service type required');
    setSaving(true);
    try {
      if (modal.editing) await api.put(`/groups/service-ratio-items/${modal.editing.id}`, form);
      else await api.post('/groups/service-ratio-items', form);
      toast.success('Saved'); setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };
  const del = async (it) => { if (!await confirm(`Delete this ${it.service_type} 1:${it.ratio_den} item?`, { danger: true })) return; try { await api.delete(`/groups/service-ratio-items/${it.id}`); load(); } catch { toast.error('Failed'); } };
  const openNew = () => { setForm(blank); setModal({}); };
  const openEdit = (it) => { setForm({ serviceType: it.service_type, ratioDen: it.ratio_den, dayOfWeek: it.day_of_week, timeOfDay: it.time_of_day, supportItemNumber: it.support_item_number || '', supportItemName: it.support_item_name || '', rate: it.rate }); setModal({ editing: it }); };

  if (loading) return <div className="flex justify-center h-32 items-center"><Loader2 className="animate-spin text-cyan-500" /></div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-400 max-w-xl">Priced NDIS line items per ratio (e.g. Community Access 1:3). The group money engine uses these for Auto-Ratio billing; missing rows fall back to (1:1 rate ÷ ratio) flagged as an estimate.</p>
        <button onClick={openNew} className="btn-primary btn-sm flex items-center gap-1.5 shrink-0"><Plus size={13} /> New Ratio Item</button>
      </div>
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-xs font-medium text-slate-500">
            <th className="text-left px-4 py-2.5">Service</th><th className="text-center px-3 py-2.5">Ratio</th>
            <th className="text-left px-3 py-2.5">Band</th><th className="text-left px-3 py-2.5">Support Item</th>
            <th className="text-right px-3 py-2.5">Rate</th><th className="px-3" />
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {items.map(it => (
              <tr key={it.id}>
                <td className="px-4 py-2.5 font-medium text-slate-700">{it.service_type}</td>
                <td className="px-3 py-2.5 text-center text-slate-600">1:{it.ratio_den}</td>
                <td className="px-3 py-2.5 text-slate-500 text-xs">{it.day_of_week} · {it.time_of_day}</td>
                <td className="px-3 py-2.5 text-slate-500"><span className="font-mono text-xs">{it.support_item_number || '—'}</span>{it.support_item_name && <div className="text-[11px] text-slate-400 truncate max-w-xs">{it.support_item_name}</div>}</td>
                <td className="px-3 py-2.5 text-right font-semibold text-slate-700">${Number(it.rate).toFixed(2)}</td>
                <td className="px-3 py-2.5"><div className="flex justify-end gap-1"><button onClick={() => openEdit(it)} className="p-1.5 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded"><Pencil size={13} /></button><button onClick={() => del(it)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"><Trash2 size={13} /></button></div></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-8 text-sm">No ratio items yet.</td></tr>}
          </tbody>
        </table>
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="font-semibold text-slate-800">{modal.editing ? 'Edit Ratio Item' : 'New Ratio Item'}</h3>
              <button onClick={() => setModal(null)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400"><X size={16} /></button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Fld label="Service Type"><input className="input-field" value={form.serviceType} onChange={e => setForm(f => ({ ...f, serviceType: e.target.value }))} /></Fld>
                <Fld label="Ratio (1:N)"><select className="input-field" value={form.ratioDen} onChange={e => setForm(f => ({ ...f, ratioDen: parseInt(e.target.value, 10) }))}>{[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>1:{n}</option>)}</select></Fld>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Fld label="Day band"><select className="input-field" value={form.dayOfWeek} onChange={e => setForm(f => ({ ...f, dayOfWeek: e.target.value }))}>{DAYS.map(d => <option key={d} value={d}>{d}</option>)}</select></Fld>
                <Fld label="Time band"><select className="input-field" value={form.timeOfDay} onChange={e => setForm(f => ({ ...f, timeOfDay: e.target.value }))}>{TIMES.map(t => <option key={t} value={t}>{t}</option>)}</select></Fld>
              </div>
              <Fld label="Support Item Number"><input className="input-field font-mono text-sm" value={form.supportItemNumber} onChange={e => setForm(f => ({ ...f, supportItemNumber: e.target.value }))} placeholder="04_102_0125_6_1" /></Fld>
              <Fld label="Support Item Name"><input className="input-field" value={form.supportItemName} onChange={e => setForm(f => ({ ...f, supportItemName: e.target.value }))} /></Fld>
              <Fld label="Rate ($/hr)"><input type="number" step="0.01" min="0" className="input-field" value={form.rate} onChange={e => setForm(f => ({ ...f, rate: e.target.value }))} /></Fld>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100">
              <button onClick={() => setModal(null)} className="btn-secondary btn-sm">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary btn-sm">{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Fld({ label, children }) { return <div><label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>{children}</div>; }
